import * as React from "react";
import { Alert } from "@mui/material";

/**
 * Google reCAPTCHA Enterprise Component with React 19 safe readiness
 * 
 * @param {string} siteKey - The reCAPTCHA site key
 * @param {function} onSuccess - Callback when reCAPTCHA is successfully completed (receives token)
 * @param {function} onExpired - Callback when reCAPTCHA expires
 * @param {string} error - Error message to display (optional)
 */
const RecaptchaEnterprise = React.forwardRef(({ siteKey, onSuccess, onExpired, error }, ref) => {
  const recaptchaWidgetId = React.useRef(null);
  const hasRenderedRef = React.useRef(false);
  const containerIdRef = React.useRef(`recaptcha-container-${Math.random().toString(36).substr(2, 9)}`);
  const onSuccessRef = React.useRef(onSuccess);
  const onExpiredRef = React.useRef(onExpired);
  const isMountedRef = React.useRef(true);

  // Always keep refs updated with latest callbacks
  onSuccessRef.current = onSuccess;
  onExpiredRef.current = onExpired;

  // Render reCAPTCHA widget once the component mounts
  React.useEffect(() => {
    isMountedRef.current = true;
    let rafId = null;

    const renderRecaptcha = () => {
      if (!isMountedRef.current) {
        return;
      }

      const container = document.getElementById(containerIdRef.current);

      if (
        window.grecaptcha?.enterprise?.render &&
        container &&
        recaptchaWidgetId.current === null
      ) {
        try {
          container.innerHTML = "";

          recaptchaWidgetId.current = window.grecaptcha.enterprise.render(
            containerIdRef.current,
            {
              sitekey: siteKey,
              callback: (token) => {
                if (onSuccessRef.current && isMountedRef.current) {
                  onSuccessRef.current(token);
                }
              },
              "expired-callback": () => {
                if (onExpiredRef.current && isMountedRef.current) {
                  onExpiredRef.current();
                }
              },
            }
          );

          hasRenderedRef.current = true;
        } catch (error) {
          console.error("reCAPTCHA render error:", error);
        }
      }
    };

    // Wait for script to load using requestAnimationFrame (React 19 safe)
    const checkAndRender = () => {
      if (!isMountedRef.current) {
        return;
      }

      if (window.grecaptcha?.enterprise?.render) {
        renderRecaptcha();
      } else {
        // Keep checking until script loads
        rafId = requestAnimationFrame(checkAndRender);
      }
    };

    // Start checking
    checkAndRender();

    return () => {
      isMountedRef.current = false;
      hasRenderedRef.current = false;
      
      // Cancel pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      // Don't try to reset the widget on unmount
      // Just clear the reference and let Google handle cleanup
      recaptchaWidgetId.current = null;
    };
  }, [siteKey]);

  // Expose reset function to parent via ref
  React.useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (
          recaptchaWidgetId.current !== null &&
          window.grecaptcha &&
          window.grecaptcha.enterprise
        ) {
          window.grecaptcha.enterprise.reset(recaptchaWidgetId.current);
        }
      },
    }),
    []
  );

  return (
    <>
      <div id={containerIdRef.current}></div>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </>
  );
});

RecaptchaEnterprise.displayName = 'RecaptchaEnterprise';

export default RecaptchaEnterprise;
